import { useTheme } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPFontIcon, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import CopyText from 'components/Utils/CopyText';
import { editIcon, globalOutcomeListStyles, globalTextStylesDisabled } from 'globalStyles';
import parse, { Element, HTMLReactParserOptions } from 'html-react-parser';
import { ReactNode, useContext, useState } from 'react';
import { clearHtmlValue, sanitizeHtml, renderISOControlReference } from 'utils/html';
import { isEmpty } from 'utils/string';
import { createBadgeJSX, getBadgeTextFromElement, isBadgeElement } from './RichTextBadgeHelper';

export interface IRichTextReadOnlyProps {
  className?: string;
  html?: string;
  placeholder?: string;
  height?: string | number;
  maxHeight?: string | number; // -1 for unlimited
  onClick?: () => void;
  children?: ReactNode;
  onBadgeClick?: (badgeText: string) => void;
  hideBorder?: boolean;
  hideEditIcon?: boolean;
  stripParagraph?: boolean;
  linkify?: boolean;
  showCopy?: boolean;
  transparent?: boolean;
}

const RichTextReadOnly = (props: IRichTextReadOnlyProps) => {
  const [hover, setHover] = useState<boolean>(false);
  const appContext = useContext(AppContext);
  const theme = useTheme();

  const onBadgeClick = (badgeText: string) => {
    if (props.onBadgeClick) {
      props.onBadgeClick(badgeText);
    }
  };

  const onClick = () => {
    if (props.onClick) {
      setHover(false);
      props.onClick();
    }
  };

  //Add a specific onClick event to all badges
  const options: HTMLReactParserOptions = {
    replace: (domNode) => {
      const node = domNode as Element;
      if (isBadgeElement(node)) {
        const badgeText = getBadgeTextFromElement(node);
        if (badgeText) {
          const newBadge = createBadgeJSX(badgeText.trim(), onBadgeClick);

          return newBadge;
        }
      }

      return renderISOControlReference(node, options);
    },
  };

  const getValue = () => {
    if (isEmpty(props.html)) {
      return <IPText styles={globalTextStylesDisabled}>{props.placeholder}</IPText>;
    } else {
      const newHtml = clearHtmlValue(props.html ?? '', props.stripParagraph ?? false, true, props.linkify ?? false);
      const clean = sanitizeHtml(newHtml ?? '');

      return parse(clean, options);
    }
  };

  // a limited height can scroll: its scrollbar gutter is kept at all times, so the text width does not change when the
  // scrollbar shows on hover
  const isScrollable = props.height !== undefined || props.maxHeight !== -1;

  //
  // Main render
  //
  return (
    <IPStack
      className={props.className}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          onClick();
        }
      }}
      onClick={() => {
        onClick();
      }}
      onMouseOver={() => {
        setHover(true);
      }}
      onMouseLeave={() => setHover(false)}
      styles={{
        root: {
          position: 'relative',
          minHeight: 32,
          border: props.hideBorder ? undefined : '1px solid grey',
          borderRadius: props.hideBorder ? undefined : '3px',
          paddingTop: '5px',
          paddingLeft: '10px',
          paddingRight: '10px',
          paddingBottom: '5px',
          color: props.html ? undefined : '#808080',
          background:
            props.transparent || props.onClick
              ? undefined
              : appContext.useDarkMode
              ? theme.palette.themeDarker
              : theme.palette.themeLighterAlt,
          '&:hover': props.onClick
            ? {
                cursor: 'text',
                'border-color': theme.palette.themeSecondary,
                background: theme.palette.neutralLighter,
                transition: 'all 0.3s ease',
              }
            : undefined,
          ...globalOutcomeListStyles,
        },
      }}
    >
      <IPStack horizontal horizontalAlign="space-between">
        <IPStack.Item
          grow
          styles={{
            root: {
              height: props.height,
              maxHeight: props.maxHeight === -1 ? undefined : props.maxHeight || 300,
              overflowY: !hover ? 'hidden' : 'auto',
              overflowX: 'hidden',
              scrollbarGutter: isScrollable ? 'stable' : undefined,
            },
          }}
        >
          <div style={{ borderWidth: '5px' }}>{getValue()}</div>
        </IPStack.Item>
        {!props.hideEditIcon && hover && props.onClick && (
          // over the right padding and the scrollbar gutter, so it takes no width from the text; the whole box
          // starts the edit, so the icon lets the pointer through to the scrollbar
          <IPFontIcon
            iconName={editIcon.iconName}
            style={{
              position: 'absolute',
              top: 6,
              right: 4,
              color: theme.palette.themePrimary,
              pointerEvents: 'none',
            }}
          />
        )}
        {props.html && props.showCopy && !props.onClick && (
          <IPStack.Item>
            <CopyText html={props.html} left={+10} top={-5} />
          </IPStack.Item>
        )}
      </IPStack>
      <IPStack.Item>{props.children}</IPStack.Item>
    </IPStack>
  );
};

export default RichTextReadOnly;
